/**
 * LearningRoadmap — Dashboard widget showing the tier ladder
 * Free → Pro → Business → Enterprise, with per-tier progress + earned
 * designation badges. Fed by /api/lms/my-roadmap.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Loader2, GraduationCap, ArrowRight, Trophy, Lock } from 'lucide-react';
import { Button } from './ui/button';

const Ring = ({ percent, done }) => {
  const size = 92, stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-[92px] h-[92px]">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth={stroke} className="text-foreground" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={done ? '#eab308' : 'hsl(var(--primary))'}
        strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={c * (1 - percent / 100)}
        strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" className="fill-foreground font-heading font-black" style={{ fontSize: 16 }}>
        {percent}%
      </text>
    </svg>
  );
};

const TierNode = ({ t, isLast }) => {
  const navigate = useNavigate();
  const active = t.percent > 0 || t.designation_earned;
  const done = t.designation_earned;

  return (
    <div className="flex items-start gap-3 flex-1 min-w-0" data-testid={`roadmap-tier-${t.tier}`}>
      <div className={`relative flex-shrink-0 ${done ? 'text-amber-500' : active ? 'text-primary' : 'text-foreground/40'}`}>
        <Ring percent={t.percent} done={done} />
        {done && (
          <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-amber-500 text-white flex items-center justify-center border-2 border-card">
            <Trophy className="w-3 h-3" />
          </div>
        )}
        {!active && !done && (
          <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-foreground/20 text-foreground/60 flex items-center justify-center border-2 border-card">
            <Lock className="w-3 h-3" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1 pt-1">
        <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-foreground/60">{t.label}</div>
        <div className="font-heading text-sm font-extrabold text-foreground tracking-tight truncate">
          {t.designation_title}
        </div>
        <div className="text-xs text-foreground/60 mt-0.5">
          {t.completed_courses}/{t.total_courses} courses
        </div>
        {done ? (
          <button
            onClick={() => navigate(`/certificate/${t.designation_cert_number}`)}
            className="text-xs font-extrabold text-amber-600 hover:underline mt-1 inline-flex items-center gap-1"
            data-testid={`roadmap-view-designation-${t.tier}`}
          >
            View designation <ArrowRight className="w-3 h-3" />
          </button>
        ) : null}
      </div>
      {!isLast && (
        <div className="hidden md:block flex-shrink-0 self-center">
          <ArrowRight className={`w-4 h-4 ${done ? 'text-amber-500' : 'text-foreground/30'}`} />
        </div>
      )}
    </div>
  );
};

const LearningRoadmap = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const token = window.localStorage.getItem('token');
        const res = await axios.get(`${API}/lms/my-roadmap`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setData(res.data);
      } catch (_) { /* silent */ } finally { setLoading(false); }
    })();
  }, [API, user]);

  if (loading) {
    return (
      <section className="pro-card p-6 sm:p-7 bg-card border border-border rounded-3xl shadow-lg" data-testid="learning-roadmap">
        <div className="flex items-center gap-2 text-foreground/60 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading roadmap…
        </div>
      </section>
    );
  }

  const tiers = data?.tiers || [];
  const next = data?.next_step;

  return (
    <section className="pro-card p-6 sm:p-7 bg-card border border-border rounded-3xl shadow-lg" data-testid="learning-roadmap">
      <header className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="eyebrow text-primary">Learning roadmap</div>
          <h2 className="font-heading text-xl sm:text-2xl font-extrabold text-foreground tracking-tight mt-2">
            Your path to <span className="text-amber-brand">Master Practitioner.</span>
          </h2>
        </div>
        <GraduationCap className="w-6 h-6 text-primary flex-shrink-0" />
      </header>

      <div className="flex flex-col md:flex-row gap-6 md:gap-3 items-stretch" data-testid="roadmap-ladder">
        {tiers.map((t, i) => (
          <TierNode key={t.tier} t={t} isLast={i === tiers.length - 1} />
        ))}
      </div>

      {next && (
        <div className="mt-6 pro-card p-4 flex items-center justify-between gap-4 bg-primary/5 border-primary/20" data-testid="roadmap-next-step">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-primary">Next milestone</div>
            <div className="font-heading font-extrabold text-foreground tracking-tight text-sm mt-0.5 truncate">
              Finish &ldquo;{next.course_title}&rdquo; to unlock the {next.designation_title} designation
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => navigate(`/lesson/${next.course_id}`)}
            className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90 flex-shrink-0"
            data-testid="roadmap-continue-btn"
          >
            Continue <ArrowRight className="ml-1 w-3.5 h-3.5" />
          </Button>
        </div>
      )}
      {!next && tiers.every((t) => t.designation_earned) && tiers.length > 0 && (
        <div className="mt-6 pro-card p-4 text-center bg-amber-500/10 border-amber-500/30" data-testid="roadmap-mastered">
          <Trophy className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <div className="font-heading font-extrabold text-foreground tracking-tight">
            You&apos;ve mastered every tier. Legend status.
          </div>
        </div>
      )}
    </section>
  );
};

export default LearningRoadmap;
